import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { socialApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { SocialTabNav } from '../components/SocialTabNav';
import { Button } from '@/components/ui/Button';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { ConnectionStatusBadge, TestStatusBadge } from './components/ConnectionStatusBadge';
import { SOCIAL_PLATFORMS } from '../config/platformCapabilities';
import { PLATFORM_SETUP_GUIDES } from './config/platformSetupGuides';
import { apiPlatformToRouteId } from './utils/platformRouteId';
import type {
  SocialConnectionOverview,
  SocialConnectionTestResult,
  SocialChannelHealth,
  SocialConnectionNotificationPrefs,
} from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import LinkIcon from '@mui/icons-material/Link';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WebhookIcon from '@mui/icons-material/Webhook';
import { SocialChannelsPage } from '../channels/SocialChannelsPage';
import { SocialWebhooksPage } from '../webhooks/SocialWebhooksPage';
import { SocialSetupPage } from '../setup/SocialSetupPage';

const TABS = [
  { id: 'overview', label: 'Overview', to: '/social/connect' },
  { id: 'channels', label: 'Connected Channels', to: '/social/connect?tab=channels' },
  { id: 'health', label: 'Connection Health', to: '/social/connect?tab=health' },
  { id: 'tokens', label: 'Token Management', to: '/social/connect?tab=tokens' },
  { id: 'webhooks', label: 'Webhook Status', to: '/social/connect?tab=webhooks' },
  { id: 'permissions', label: 'Permissions', to: '/social/connect?tab=permissions' },
  { id: 'tests', label: 'Diagnostics', to: '/social/connect?tab=tests' },
  { id: 'logs', label: 'Connection Logs', to: '/social/connect?tab=logs' },
  { id: 'alerts', label: 'Notifications', to: '/social/connect?tab=alerts' },
  { id: 'guides', label: 'Guides', to: '/social/connect?tab=guides' },
  { id: 'troubleshoot', label: 'Troubleshooting', to: '/social/connect?tab=troubleshoot' },
];

const NOTIF_KEY = 'social.connection.notifications';

function loadNotifPrefs(): SocialConnectionNotificationPrefs {
  try {
    const raw = localStorage.getItem(NOTIF_KEY);
    return raw
      ? (JSON.parse(raw) as SocialConnectionNotificationPrefs)
      : {
          emailOnTokenExpiry: true,
          emailOnWebhookFailure: true,
          emailOnDisconnect: true,
          inAppAlerts: true,
        };
  } catch {
    return { emailOnTokenExpiry: true, emailOnWebhookFailure: true, emailOnDisconnect: true, inAppAlerts: true };
  }
}

export function SocialConnectionCenterPage() {
  const [params] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const [overview, setOverview] = useState<SocialConnectionOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedAccountId, setSelectedAccountId] = useState<number | ''>('');
  const [tests, setTests] = useState<SocialConnectionTestResult[]>([]);
  const [testing, setTesting] = useState(false);
  const [notif, setNotif] = useState(loadNotifPrefs);
  const [logs, setLogs] = useState(overview?.recentLogs ?? []);
  const [logsPage, setLogsPage] = useState(0);
  const [logsTotal, setLogsTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await socialApi.connectionOverview();
      setOverview(data);
      setLogs(data.recentLogs ?? []);
      if (data.channels.length > 0 && selectedAccountId === '') {
        setSelectedAccountId(data.channels[0].accountId);
      }
    } catch (err) {
      showApiError(err, 'Failed to load connection center');
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), 60000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (tab !== 'logs') return;
    socialApi
      .connectionLogs({ page: logsPage, size: 25 })
      .then((page) => {
        setLogs(page.content ?? []);
        setLogsTotal(page.totalElements ?? 0);
      })
      .catch(() => undefined);
  }, [tab, logsPage]);

  const runTests = async (type?: string) => {
    if (selectedAccountId === '') return;
    setTesting(true);
    try {
      const res = await socialApi.runConnectionTests(Number(selectedAccountId), type);
      setTests(res);
      notify.success('Diagnostics complete');
    } catch (err) {
      showApiError(err, 'Diagnostics failed');
    } finally {
      setTesting(false);
    }
  };

  const refreshToken = async (accountId: number) => {
    try {
      await socialApi.refreshChannelToken(accountId);
      notify.success('Token refresh attempted');
      void load();
    } catch (err) {
      showApiError(err, 'Token refresh failed — reconnect channel');
    }
  };

  const saveNotif = (next: SocialConnectionNotificationPrefs) => {
    setNotif(next);
    localStorage.setItem(NOTIF_KEY, JSON.stringify(next));
    notify.success('Notification preferences saved');
  };

  const channels = overview?.channels ?? [];

  return (
    <div>
      <PageHeader
        title="Social Connection Center"
        description="Connect, validate, and monitor every social channel — no developer knowledge required"
        breadcrumbs={[{ label: 'Social', to: '/social' }, { label: 'Connection Center' }]}
        actions={
          <Button variant="secondary" onClick={() => void load()} loading={loading}>
            Refresh status
          </Button>
        }
      />
      <SocialTabNav tabs={TABS} className="mb-4" />

      {tab === 'overview' && (
        <>
          <KpiGrid columns={4}>
            <StatCard title="Connected" value={overview?.connectedChannels ?? '—'} icon={<LinkIcon />} />
            <StatCard title="Avg health" value={overview ? `${overview.averageHealthScore}%` : '—'} icon={<HealthAndSafetyIcon />} />
            <StatCard title="Token issues" value={overview?.tokenIssues ?? '—'} icon={<VpnKeyIcon />} />
            <StatCard title="Webhook issues" value={overview?.webhookIssues ?? '—'} icon={<WebhookIcon />} />
          </KpiGrid>

          {overview && overview.alerts.length > 0 && (
            <div className="card mt-4 border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Active alerts</h3>
              <ul className="mt-2 space-y-2">
                {overview.alerts.map((a) => (
                  <li key={`${a.code}-${a.accountId}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span>
                      <strong>{a.title}</strong> — {a.message}
                    </span>
                    <Link to={`/social/connect/wizard/${apiPlatformToRouteId(a.platform ?? 'FACEBOOK')}`} className="text-link text-xs">
                      Fix →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {SOCIAL_PLATFORMS.map((p) => {
              const ch = channels.find((c) => c.platform.toUpperCase().includes(p.apiPlatform.split('_')[0] ?? ''));
              return (
                <Link
                  key={p.id}
                  to={`/social/connect/wizard/${p.id}`}
                  className="card flex items-center gap-3 p-4 hover:ring-2 hover:ring-primary-500/30"
                >
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-lg text-xs font-bold text-white"
                    style={{ backgroundColor: p.brandColor }}
                  >
                    {p.label.slice(0, 2)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{p.label}</p>
                    <p className="text-xs text-slate-500">
                      {ch?.connected ? 'Connected' : 'Not connected'} · Setup wizard
                    </p>
                  </div>
                  {ch && <ConnectionStatusBadge score={ch.healthScore} status={ch.healthStatus} />}
                </Link>
              );
            })}
          </div>
        </>
      )}

      {tab === 'channels' && <SocialChannelsPage embedded />}

      {tab === 'health' && (
        <div className="space-y-3">
          {channels.length === 0 && <p className="text-sm text-slate-500">No channels yet. Start a setup wizard.</p>}
          {channels.map((ch) => (
            <ChannelHealthCard key={ch.accountId} channel={ch} onRefresh={refreshToken} />
          ))}
        </div>
      )}

      {tab === 'tokens' && (
        <div className="space-y-3">
          {channels.map((ch) => (
            <div key={ch.accountId} className="card p-4 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{ch.platform} — {ch.accountName}</p>
                <ConnectionStatusBadge score={ch.healthScore} />
              </div>
              <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                <div><dt className="text-slate-500">Token status</dt><dd>{ch.tokenOk ? 'Valid' : 'Expired / missing'}</dd></div>
                <div><dt className="text-slate-500">Expires</dt><dd>{ch.tokenExpiry ? new Date(ch.tokenExpiry).toLocaleString() : '—'}</dd></div>
                <div><dt className="text-slate-500">Refresh token</dt><dd>{ch.refreshTokenPresent ? 'Present' : 'Missing'}</dd></div>
                <div><dt className="text-slate-500">Scopes</dt><dd className="truncate">{ch.scopes ?? 'Default / demo'}</dd></div>
              </dl>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => void refreshToken(ch.accountId)}>Refresh token</Button>
                <Link to={`/social/connect/wizard/${apiPlatformToRouteId(ch.platform)}`}>
                  <Button size="sm" variant="secondary">Reconnect wizard</Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'webhooks' && <SocialWebhooksPage embedded />}

      {tab === 'permissions' && (
        <div className="space-y-3">
          {channels.map((ch) => (
            <div key={ch.accountId} className="card p-4">
              <p className="font-semibold text-sm">{ch.platform} — {ch.accountName}</p>
              <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                <PermissionRow label="Publishing" ok={ch.publishingOk} />
                <PermissionRow label="Inbox / DM" ok={ch.inboxOk} />
                <PermissionRow label="Comments" ok={ch.commentsOk} />
                <PermissionRow label="Reviews" ok={ch.reviewsOk} />
                <PermissionRow label="Analytics" ok={ch.analyticsOk} />
                <PermissionRow label="CRM integration" ok={ch.connected} />
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === 'tests' && (
        <div className="card p-4">
          <div className="mb-4 flex flex-wrap gap-3">
            <select
              className="input max-w-xs"
              value={selectedAccountId === '' ? '' : String(selectedAccountId)}
              onChange={(e) => setSelectedAccountId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Select channel</option>
              {channels.map((c) => (
                <option key={c.accountId} value={c.accountId}>
                  {c.platform} — {c.accountName}
                </option>
              ))}
            </select>
            <Button onClick={() => void runTests()} loading={testing}>Run all tests</Button>
            {['TOKEN', 'WEBHOOK', 'PERMISSION', 'API', 'FEATURE'].map((t) => (
              <Button key={t} size="sm" variant="secondary" onClick={() => void runTests(t)}>
                {t}
              </Button>
            ))}
          </div>
          <ul className="space-y-2">
            {tests.map((t) => (
              <li key={t.testType} className="rounded border p-3 text-sm dark:border-slate-700">
                <div className="flex justify-between">
                  <span className="font-medium">{t.testType}</span>
                  <TestStatusBadge status={t.status} />
                </div>
                <p className="text-slate-600 dark:text-slate-400">{t.message}</p>
                {t.fixAction && <p className="mt-1 text-xs text-primary-600">{t.fixAction}</p>}
              </li>
            ))}
            {tests.length === 0 && <p className="text-slate-500">Run diagnostics to see results.</p>}
          </ul>
        </div>
      )}

      {tab === 'logs' && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900">
              <tr>
                <th className="p-2 text-left">Time</th>
                <th className="p-2 text-left">Platform</th>
                <th className="p-2 text-left">Action</th>
                <th className="p-2 text-left">Detail</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} className="border-t dark:border-slate-800">
                  <td className="p-2">{new Date(l.createdAt).toLocaleString()}</td>
                  <td className="p-2">{l.platform ?? '—'}</td>
                  <td className="p-2">{l.actionType}</td>
                  <td className="p-2">{l.actionDetail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {logs.length === 0 && <p className="p-4 text-sm text-slate-500">No audit entries yet.</p>}
          {logsTotal > 25 && (
            <div className="flex justify-between border-t p-3 text-sm dark:border-slate-800">
              <button
                type="button"
                className="text-primary-600 disabled:opacity-40"
                disabled={logsPage === 0}
                onClick={() => setLogsPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </button>
              <span className="text-slate-500">
                Page {logsPage + 1} · {logsTotal} entries
              </span>
              <button
                type="button"
                className="text-primary-600 disabled:opacity-40"
                disabled={(logsPage + 1) * 25 >= logsTotal}
                onClick={() => setLogsPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'alerts' && (
        <div className="card p-4 space-y-4">
          <h3 className="text-sm font-semibold">Alert preferences</h3>
          {(
            [
              ['emailOnTokenExpiry', 'Email when token expires or expiring soon'],
              ['emailOnWebhookFailure', 'Email on webhook failure'],
              ['emailOnDisconnect', 'Email when connection lost'],
              ['inAppAlerts', 'In-app notifications'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notif[key]}
                onChange={(e) => saveNotif({ ...notif, [key]: e.target.checked })}
              />
              {label}
            </label>
          ))}
          <p className="text-xs text-slate-500">
            Email delivery uses your organization notification settings. In-app alerts appear on the Social dashboard.
          </p>
        </div>
      )}

      {tab === 'guides' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {Object.values(PLATFORM_SETUP_GUIDES).map((g) => (
            <div key={g.platformId} className="card p-4">
              <p className="font-semibold">{g.label}</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{g.overview.slice(0, 140)}…</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to={`/social/connect/guide/${g.platformId}`}>
                  <Button size="sm">Read setup guide</Button>
                </Link>
                <Link to={`/social/connect/wizard/${g.platformId}`}>
                  <Button size="sm" variant="secondary">
                    Guided wizard
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'troubleshoot' && (
        <div className="space-y-4">
          {overview?.alerts.length === 0 && (
            <div className="card p-6 text-sm text-slate-600 dark:text-slate-400">
              No active connection issues. For step-by-step fixes, open{' '}
              <Link to="/social/connect?tab=guides" className="text-link font-medium">
                setup guides
              </Link>
              .
            </div>
          )}
          {overview?.alerts.map((a) => (
            <div key={a.code + String(a.accountId)} className="card p-4">
              <p className="font-semibold">{a.title}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">{a.message}</p>
              <p className="mt-2 text-sm">
                <strong>What to do:</strong> {a.fixAction}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {a.accountId && (
                  <Button size="sm" onClick={() => void refreshToken(a.accountId!)}>
                    Refresh access
                  </Button>
                )}
                <Link to={`/social/connect/guide/${apiPlatformToRouteId(a.platform ?? 'FACEBOOK')}`}>
                  <Button size="sm" variant="secondary">
                    Read guide
                  </Button>
                </Link>
                <Link to={`/social/connect/wizard/${apiPlatformToRouteId(a.platform ?? 'FACEBOOK')}`}>
                  <Button size="sm" variant="secondary">
                    Guided reconnect
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'integrations' && <SocialSetupPage embedded tab="integrations" />}
    </div>
  );
}

function ChannelHealthCard({
  channel: ch,
  onRefresh,
}: {
  channel: SocialChannelHealth;
  onRefresh: (id: number) => void;
}) {
  return (
    <div className="card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-semibold">{ch.platform}</p>
          <p className="text-sm text-slate-500">{ch.accountName}</p>
        </div>
        <ConnectionStatusBadge score={ch.healthScore} status={ch.healthStatus} />
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-xs">
        <span>OAuth: {ch.oauthOk ? '✓' : '✗'}</span>
        <span>Token: {ch.tokenOk ? '✓' : '✗'}</span>
        <span>Webhook: {ch.webhookOk ? '✓' : '✗'}</span>
        <span>Events 24h: {ch.webhookEvents24h}</span>
      </div>
      {ch.issues.length > 0 && (
        <ul className="mt-2 list-disc pl-5 text-xs text-amber-800 dark:text-amber-200">
          {ch.issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="secondary" onClick={() => onRefresh(ch.accountId)}>
          Refresh token
        </Button>
        <Link to={`/social/connect/wizard/${apiPlatformToRouteId(ch.platform)}`}>
          <Button size="sm">Setup wizard</Button>
        </Link>
      </div>
    </div>
  );
}

function PermissionRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <li className={ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}>
      {ok ? '✓' : '○'} {label}
    </li>
  );
}
